import React, { useState, useEffect } from 'react';
import {
  Search, X, Star, Leaf, Utensils, Plus, Minus, Trash2,
  CheckCircle, Loader, MapPin, Edit, Home, PlusCircle, Lock, Drumstick, ChevronDown
} from 'lucide-react';
import './OrderFood.css';
import LoginModal from './Login';
import apiClient from '../apis/apiClient';
import { isRestaurantOpen, getRestaurantStatusMessage } from '../utils/timeUtils';
import { DateTime } from 'luxon';


const OrderFood = () => {
  // State management
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingDishes, setLoadingDishes] = useState(true);
  const [error, setError] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [userId, setUserId] = useState(localStorage.getItem("userId") ? parseInt(localStorage.getItem("userId")) : null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [newAddress, setNewAddress] = useState({
    type: 'Home',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    isDefault: false
  });
  const [editingAddress, setEditingAddress] = useState(null);
  const [showAddEditAddressModal, setShowAddEditAddressModal] = useState(false);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  // Store the selected address details for consistent display
  const [selectedAddressDetails, setSelectedAddressDetails] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const email = user?.email;
  const name = user?.name;

  // Category configuration
  const CATEGORY_ORDER = [
    'SUPPEN',
    'SALATE',
    'VORSPEISEN',
    'INDISCHE BROTSPEZIALITÄTEN',
    'VEGETARISCH',
    'HÄHNCHENGERICHTE',
    'LAMMGERICHTE',
    'FISCHGERICHTE',
    'PASTA',
    'TANDOORI SPEZIALITÄTEN',
    'REIS SPEZIALITÄTEN',
    'KINDER GERICHTE',
    'NACHSPEISEN',
    'INDISCHE GETRÄNKE'
  ];

  // Map real category names (with descriptions) to base names
  const CATEGORY_MAP = {
    'SUPPEN': 'SUPPEN',
    'SALATE': 'SALATE',
    'VORSPEISEN': 'VORSPEISEN',
    'PASTA': 'PASTA',
    'NACHSPEISEN': 'NACHSPEISEN',
    'FISCHGERICHTE INKLUSIVE BASMATI REIS': 'FISCHGERICHTE',
    'HÄHNCHENGERICHTE INKLUSIVE BASMATI REIS': 'HÄHNCHENGERICHTE',
    'INDISCHE BROTSPEZIALITÄTEN': 'INDISCHE BROTSPEZIALITÄTEN',
    'INDISCHE GETRÄNKE': 'INDISCHE GETRÄNKE',
    'LAMMGERICHTE INKLUSIVE BASMATI REIS': 'LAMMGERICHTE',
    'REIS SPEZIALITÄTEN': 'REIS SPEZIALITÄTEN',
    'KINDER GERICHTE': 'KINDER GERICHTE',
    'KINDERGERICHTE': 'KINDER GERICHTE',
    'TANDOORI SPEZIALITÄTEN TANDOORI GERICHTE WERDEN ZUBEREITET, INDEM SIE STUNDENLANG IN AROMATISCHEN MARINADEN EINGELEGT UND ANSCHLIESSEND IM TANDOORI OFEN GEGRILLT WERDEN. MIT SALAT, NAAN UND BASMATI REIS': 'TANDOORI SPEZIALITÄTEN',
    'VEGETARISCH INKLUSIVE BASMATI REIS': 'VEGETARISCH',
    'HEIßE GETRÄNKE': 'INDISCHE GETRÄNKE' // Map HEIßE GETRÄNKE to INDISCHE GETRÄNKE
  };

  // Format price in euros
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price);
  };

  // API configuration
  const api = apiClient;

  // Enhanced error handler
  const handleApiError = (error, context) => {
    console.error(`API Error in ${context}:`, error);

    let errorMessage = 'An error occurred';
    if (error.response) {
      if (error.response.status === 401) {
        handleLogout();
        return 'Session expired. Please login again.';
      }
      if (error.response.status === 404 && context === 'Loading addresses') {
        return null; // Don't show error for 404 on addresses
      }
      errorMessage = error.response.data?.message ||
        `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'No response from server. Check your network connection.';
    } else {
      errorMessage = error.message || 'Request setup failed';
    }

    if (!(error.response && error.response.status === 404)) {
      setError(`${context}: ${errorMessage}`);
    }
    return errorMessage;
  };

  // Handle login success
  const handleLoginSuccess = (token, userId) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("userId", userId);
    setAuthToken(token);
    setUserId(parseInt(userId));
    setShowLoginModal(false);
    fetchCartItems();
    fetchUserAddresses();
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userId");
    setAuthToken(null);
    setUserId(null);
    setCart([]);
    setAddresses([]);
    setSelectedAddress(null);
    setSelectedAddressDetails(null);
  };

  // Helper function to normalize address format
  const normalizeAddress = (address) => {
    if (!address) return null;

    return {
      streetAddress: address.streetAddress || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || ''
    };
  };

  // Update selected address details when selectedAddress changes
  useEffect(() => {
    if (selectedAddress && addresses.length > 0) {
      const address = addresses.find(a => a.id === selectedAddress);
      if (address) {
        setSelectedAddressDetails(normalizeAddress(address));
      }
    } else {
      setSelectedAddressDetails(null);
    }
  }, [selectedAddress, addresses]);

  // Fetch user addresses
  const fetchUserAddresses = async () => {
    if (!userId) return;

    try {
      setLoadingAddresses(true);
      const response = await api.get(`/addre/user/${userId}`);

      if (response.data && Array.isArray(response.data)) {
        setAddresses(response.data);
        const defaultAddress = response.data.find(addr => addr.isDefault);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        } else if (response.data.length > 0) {
          setSelectedAddress(response.data[0].id);
        }
      }
    } catch (error) {
      const errorMsg = handleApiError(error, 'Loading addresses');
      if (error.response && error.response.status === 404) {
        // If addresses endpoint returns 404, set empty addresses array
        setAddresses([]);
      }
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Handle address input change
  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save address (create or update)
  const saveAddress = async () => {
    try {
      setIsLoading(true);
      let response;

      if (editingAddress) {
        response = await api.put(`/addre/${editingAddress.id}`, newAddress);
        setAddresses(prev =>
          prev.map(addr => addr.id === editingAddress.id ? response.data : addr)
        );
      } else {
        response = await api.post('/addre', {
          ...newAddress,
          userId: userId
        });
        setAddresses(prev => [...prev, response.data]);
      }

      if (addresses.length === 0 || newAddress.isDefault) {
        setSelectedAddress(response.data.id);
      }

      setShowAddEditAddressModal(false);
      resetAddressForm();
    } catch (error) {
      handleApiError(error, 'Saving address');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete address
  const deleteAddress = async (id) => {
    try {
      setIsLoading(true);

      await api.delete(`/addre/${id}`);

      const response = await api.get(`/addre/user/${userId}`);
      const updatedAddresses = response.data;
      setAddresses(updatedAddresses);

      if (selectedAddress === id) {
        if (updatedAddresses.length > 0) {
          setSelectedAddress(updatedAddresses[0].id);
        } else {
          setSelectedAddress(null);
        }
      }

    } catch (error) {
      handleApiError(error, 'Deleting address');
    } finally {
      setIsLoading(false);
    }
  };

  // Set default address
  const setDefaultAddress = async (id) => {
    try {
      setIsLoading(true);
      // For the new API, we'll need to update the address to set isDefault=true
      // and update other addresses to set isDefault=false
      const addressToUpdate = addresses.find(addr => addr.id === id);

      if (addressToUpdate) {
        // First, update the selected address to be default
        await api.put(`/addre/${id}`, {
          ...addressToUpdate,
          isDefault: true
        });

        // Then update any other addresses that were default to not be default
        const otherDefaultAddresses = addresses.filter(addr =>
          addr.id !== id && addr.isDefault
        );

        for (const addr of otherDefaultAddresses) {
          await api.put(`/addre/${addr.id}`, {
            ...addr,
            isDefault: false
          });
        }
      }

      // Refresh addresses
      await fetchUserAddresses();

    } catch (error) {
      handleApiError(error, 'Setting default address');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset address form
  const resetAddressForm = () => {
    setNewAddress({
      type: 'Home',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
      isDefault: false
    });
    setEditingAddress(null);
  };

  // Edit address
  const editAddress = (address) => {
    setEditingAddress(address);
    setNewAddress({
      type: address.type || 'Home',
      streetAddress: address.streetAddress || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || '',
      phoneNumber: address.phoneNumber || '',
      isDefault: address.isDefault || false
    });
    setShowAddEditAddressModal(true);
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.streetAddress || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}`;
  };

  // Fetch cart items
  const fetchCartItems = async () => {
    if (!userId) return;

    try {
      setCartLoading(true);
      const response = await api.get(`/cart/user/${userId}`);

      if (response.status === 404) {
        setCart([]);
        return;
      }

      if (!response.data) {
        throw new Error('Invalid cart data');
      }

      setCart(response.data);
    } catch (error) {
      handleApiError(error, 'Loading cart');
    } finally {
      setCartLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingDishes(true);
        setError(null);

        const [dishesResponse, categoriesResponse] = await Promise.all([
          api.get('/dishes'),
          api.get('/categories')
        ]);

        if (!dishesResponse?.data || !categoriesResponse?.data) {
          throw new Error('Invalid data received from server');
        }

        setDishes(dishesResponse.data);
        setCategories(categoriesResponse.data);

      } catch (error) {
        handleApiError(error, 'Loading menu data');
      } finally {
        setLoadingDishes(false);
      }
    };

    fetchData();
  }, []);

  // Fetch cart items and addresses when userId changes
  useEffect(() => {
    if (!userId) return;

    fetchCartItems();
    fetchUserAddresses();
  }, [userId]);

  // Helper functions
  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return <Leaf />;
    const lowerName = categoryName.toLowerCase();
    if (lowerName.includes('kinder')) return <Utensils />;
    if (lowerName.includes('appetizer') || lowerName.includes('vorspeisen')) return <Utensils />;
    if (lowerName.includes('main') || lowerName.includes('gericht')) return <Drumstick />;
    if (lowerName.includes('dessert') || lowerName.includes('nachspeisen')) return <Star />;
    if (lowerName.includes('beverage') || lowerName.includes('getränk')) return <Leaf />;
    if (lowerName.includes('suppe')) return <Utensils />;
    if (lowerName.includes('salat')) return <Leaf />;
    return <Leaf />;
  };

  const getRandomColor = () => {
    const colors = ['#d4af37', '#4a8f29', '#c8860d', '#c0392b', '#8e44ad', '#2980b9', '#e67e22'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Function to map API category names to our base category names
  const getBaseCategoryName = (apiCategoryName) => {
    if (!apiCategoryName) return null;

    const upperName = apiCategoryName.toUpperCase().trim();

    // Try exact match first
    if (CATEGORY_MAP[upperName]) {
      return CATEGORY_MAP[upperName];
    }

    // Try partial match for categories that might have variations
    for (const [key, value] of Object.entries(CATEGORY_MAP)) {
      if (upperName.includes(key) || key.includes(upperName)) {
        return value;
      }
    }

    // Try matching by starting with the base category name
    for (const baseCategory of CATEGORY_ORDER) {
      if (upperName.startsWith(baseCategory)) {
        return baseCategory;
      }
    }

    return null;
  };

  // Create the menu data in the specified order
  const menuData = CATEGORY_ORDER.map(categoryName => {
    // Find matching categories from API
    const matchingCategories = categories.filter(cat => {
      const baseName = getBaseCategoryName(cat?.name);
      return baseName === categoryName;
    });

    // Get all dishes that belong to any of the matching categories
    let categoryDishes = [];
    matchingCategories.forEach(cat => {
      const dishesForCategory = dishes.filter(dish => dish?.category?.id === cat?.id);
      categoryDishes = [...categoryDishes, ...dishesForCategory];
    });

    // Use the first matching category's name for display, or fallback to our category name
    const displayName = matchingCategories.length > 0
      ? matchingCategories[0].name.toUpperCase()
      : categoryName;

    // Check if this is a vegetarian category
    const isVegetarianCategory = categoryName.toLowerCase().includes('vegetarisch');

    return {
      title: displayName,
      baseTitle: categoryName,
      icon: getCategoryIcon(categoryName),
      color: getRandomColor(),
      isVegetarian: isVegetarianCategory,
      items: categoryDishes.map(dish => ({
        id: dish.dishid,
        name: dish.dishname,
        price: parseFloat(dish.dishprice) || 0,
        description: dish.dishdescription,
        imageUrl: dish.dishimageUrl,
        vegetarian: dish.vegetarian || false
      }))
    };
  }).filter(section => section.items.length > 0); // Only include categories that have dishes

  // Filter menu items
  const filteredMenuData = menuData.map(section => ({
    ...section,
    items: section.items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (activeFilter === 'all' ||
        (activeFilter === 'vegetarian' && item.vegetarian))
    )
  })).filter(section => section.items.length > 0);

  // Cart operations
  const addToCart = async (item) => {
    if (!isRestaurantOpen()) {
      alert(getRestaurantStatusMessage());
      return;
    }

    if (!userId) {
      setShowLoginModal(true);
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.post('/cart', {
        userId,
        dish: { dishid: item.id },
        itemName: item.name,
        price: item.price,
        quantity: 1
      });

      setCart(prev => {
        const existingItem = prev.find(cartItem => cartItem.dish?.dishid === item.id);
        if (existingItem) {
          return prev.map(cartItem =>
            cartItem.dish?.dishid === item.id
              ? { ...cartItem, quantity: cartItem.quantity + 1 }
              : cartItem
          );
        }
        return [...prev, response.data];
      });
    } catch (error) {
      handleApiError(error, 'Adding to cart');
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (id) => {
    try {
      setIsLoading(true);
      await api.delete(`/cart/${id}`);
      setCart(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      handleApiError(error, 'Removing from cart');
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (id, change) => {
    try {
      setIsLoading(true);
      const itemToUpdate = cart.find(item => item.id === id);
      if (!itemToUpdate) return;

      const newQuantity = Math.max(1, itemToUpdate.quantity + change);

      const response = await api.patch(`/cart/${id}`, {
        quantity: newQuantity
      });

      setCart(prev =>
        prev.map(item =>
          item.id === id
            ? response.data
            : item
        ).filter(item => item.quantity > 0)
      );
    } catch (error) {
      handleApiError(error, 'Updating quantity');
    } finally {
      setIsLoading(false);
    }
  };

  // Order functions
  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const proceedToPay = async () => {
    if (!userId || !authToken) {
      setShowLoginModal(true);
      return;
    }

    if (!isRestaurantOpen()) {
      alert(getRestaurantStatusMessage());
      return;
    }

    if (!selectedAddress && addresses.length > 0) {
      setShowAddressModal(true);
      return;
    }

    // Validate that selected address has a phone number
    if (selectedAddress) {
      const address = addresses.find(addr => addr.id === selectedAddress);
      if (!address || !address.phoneNumber) {
        alert('Please add a phone number to your delivery address before checkout.');
        setShowAddressModal(true);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    const user = JSON.parse(localStorage.getItem("user"));
    const email = user?.email;

    if (!email) {
      setIsLoading(false);
      alert("Email not found. Please login again.");
      return;
    }

    try {
      const orderItems = cart.map(item => ({
        dishId: item.dish?.dishid || item.id,
        itemName: item.itemName || item.name,
        price: item.price,
        quantity: item.quantity
      }));

      const selectedAddrObj = addresses.find(addr => addr.id === selectedAddress);
      const phone = selectedAddrObj?.phoneNumber || '';

      // Send address ID and phone number in order
      const orderPayload = {
        order: {
          userId,
          name,
          email,
          phoneNumber: phone,
          totalAmount: calculateTotal(),
          status: "PLACED",
          orderDate: new Date().toISOString(),
          shippingAddress: { id: selectedAddress }
        },
        items: orderItems
      };

      const orderResponse = await api.post('/order', orderPayload);

      // Clear cart
      try {
        await api.delete(`/cart/clear/${userId}`);
      } catch (clearError) {
        console.error("Error clearing cart:", clearError);
      }

      // ALWAYS use the selected address details for display consistency
      const displayAddress = selectedAddressDetails || {
        streetAddress: "Default Address",
        city: "Hyderabad",
        state: "Andhra Pradesh",
        zipCode: "500033"
      };

      setOrderDetails({
        orderNumber: orderResponse.data?.order?.id || orderResponse.data?.id || Math.floor(Math.random() * 1000000),
        items: cart.map(item => ({
          name: item.itemName || item.name,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: calculateTotal(),
        total: calculateTotal(),
        date: DateTime.now().setZone('Europe/Berlin').toLocaleString({ year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        status: orderResponse.data?.order?.status || orderResponse.data?.status || 'PLACED',
        deliveryTime: DateTime.now().setZone('Europe/Berlin').plus({ minutes: 45 }).toFormat('HH:mm'),
        deliveryAddress: displayAddress
      });

      setCart([]);
      setOrderConfirmed(true);
    } catch (error) {
      handleApiError(error, 'Placing order');
    } finally {
      setIsLoading(false);
    }
  };

  // UI helpers
  const toggleCategory = (title) => {
    setActiveCategory(activeCategory === title ? null : title);
  };

  const continueShopping = () => {
    setOrderConfirmed(false);
    setOrderDetails(null);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Loading state
  if (loadingDishes || cartLoading) {
    return (
      <div className="loading-container">
        <Loader className="spinner" size={48} />
        <p>Loading menu...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => setError(null)} className="dismiss-error-btn">
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // Order confirmation
  if (orderConfirmed && orderDetails) {
    return (
      <div className="order-confirmation-container">
        <div className="order-confirmation">
          <div className="confirmation-content">
            <div className="confirmation-icon-wrapper">
              <CheckCircle className="confirmation-icon" />
            </div>

            <h2 className="confirmation-title">Bestellung bestätigt!</h2>

            <p className="confirmation-message">
              Vielen Dank für Ihre Bestellung!
              Ihre Bestellung ist in ca. 30–45 Minuten abholbereit.
              Bitte holen Sie Ihre Bestellung im Restaurant ab.
            </p>

            <div className="order-details-card">
              <div className="order-header">
                <h3 className="order-number">
                  Bestellung #{orderDetails.orderNumber}
                </h3>

                <div className="order-meta">
                  <p>
                    <span>Abholzeit:</span> {orderDetails.deliveryTime}
                  </p>
                  <p>
                    <span>Bestellt am:</span> {orderDetails.date}
                  </p>
                  <p>
                    <span>Status:</span>
                    <span className="order-status preparing">
                      In Vorbereitung
                    </span>
                  </p>
                </div>
              </div>

              {/* Pickup Info Instead of Delivery Address */}
              <div className="order-address-section">
                <h4>Abholung im Restaurant</h4>
                <div className="address-display">
                  <MapPin size={16} className="address-icon" />
                  <div>
                    <p>Bitte kommen Sie zur angegebenen Abholzeit ins Restaurant.</p>
                  </div>
                </div>
              </div>

              <div className="order-items-section">
                <h4>Ihre Bestellung</h4>
                <div className="order-items-list">
                  {orderDetails.items.map((item, index) => (
                    <div key={index} className="order-item">
                      <div className="item-info">
                        <span className="item-quantity">{item.quantity}x</span>
                        <span className="item-name">{item.name}</span>
                      </div>
                      <span className="item-total">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-summary-section">
                <div className="summary-row">
                  <span>Gesamtbetrag</span>
                  <span>{formatPrice(orderDetails.total)}</span>
                </div>
              </div>
            </div>

            <button onClick={continueShopping} className="continue-shopping-btn">
              Neue Bestellung aufgeben
            </button>
          </div>
        </div>
      </div>

    );
  }

  // Address Selection Modal
  const AddressModal = () => (
    <div className="modal-overlay">
      <div className="address-modal">
        <div className="modal-header">
          <h3>Telefonadresse auswählen</h3>
          <button onClick={() => setShowAddressModal(false)} className="close-modal">
            <X size={20} />
          </button>
        </div>

        <div className="address-list">
          {loadingAddresses ? (
            <div className="loading-addresses">
              <Loader className="spinner" size={24} />
              <p>Ihre Adressen werden geladen...</p>
            </div>
          ) : addresses.length === 0 ? (
            <div className="no-addresses">
              <p>Keine Adressen gefunden. Bitte fügen Sie eine Adresse hinzu, um fortzufahren.</p>
              <button
                onClick={() => {
                  setShowAddressModal(false);
                  setShowAddEditAddressModal(true);
                }}
                className="add-address-btn"
              >
                <PlusCircle size={16} style={{ marginRight: '8px' }} />
                Neue Adresse hinzufügen
              </button>
            </div>
          ) : (
            <>
              {addresses.map(address => (
                <div
                  key={address.id}
                  className={`address-card ${selectedAddress === address.id ? 'selected' : ''}`}
                  onClick={() => setSelectedAddress(address.id)}
                >
                  <div className="address-radio">
                    <div className={`radio-circle ${selectedAddress === address.id ? 'checked' : ''}`}>
                      {selectedAddress === address.id && <div className="radio-dot" />}
                    </div>
                  </div>

                  <div className="address-details">
                    <div className="address-header">
                      <h4>
                        {address.type || 'Meine Adresse'}
                        {address.isDefault && (
                          <span className="default-badge">Standard</span>
                        )}
                      </h4>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          editAddress(address);
                        }}
                        className="edit-address-btn"
                      >
                        <Edit size={14} />
                      </button>
                    </div>

                    <p>{formatAddress(address)}</p>

                    {address.phoneNumber && (
                      <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                        📱 {address.phoneNumber}
                      </p>
                    )}

                    <div className="address-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDefaultAddress(address.id);
                        }}
                        className="set-default-btn"
                        disabled={address.isDefault}
                      >
                        {address.isDefault ? 'Standard' : 'Als Standard festlegen'}
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAddress(address.id);
                        }}
                        className="delete-address-btn"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={() => {
                  setShowAddressModal(false);
                  setShowAddEditAddressModal(true);
                }}
                className="add-new-address-btn"
              >
                <PlusCircle size={16} style={{ marginRight: '8px' }} />
                Neue Adresse hinzufügen
              </button>
            </>
          )}
        </div>

        {addresses.length > 0 && (
          <div className="modal-actions">
            <button
              onClick={() => setShowAddressModal(false)}
              className="cancel-btn"
            >
              Abbrechen
            </button>

            <button
              onClick={() => {
                setShowAddressModal(false);
              }}
              className="confirm-btn"
              disabled={!selectedAddress}
            >
              Auswahl bestätigen
            </button>
          </div>
        )}
      </div>
    </div>
  );


  // Add/Edit Address Modal
  const AddEditAddressModal = () => {
    const [formData, setFormData] = useState({
      type: 'Home',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
      isDefault: false
    });

    useEffect(() => {
      if (editingAddress) {
        setFormData({
          type: editingAddress.type || 'Home',
          streetAddress: editingAddress.streetAddress || '',
          city: editingAddress.city || '',
          state: editingAddress.state || '',
          zipCode: editingAddress.zipCode || '',
          phoneNumber: editingAddress.phoneNumber || '',
          isDefault: editingAddress.isDefault || false
        });
      } else {
        setFormData({
          type: 'Home',
          streetAddress: '',
          city: '',
          state: '',
          zipCode: '',
          phoneNumber: '',
          isDefault: false
        });
      }
    }, [editingAddress]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };

    const handleSubmit = async () => {
      try {
        setIsLoading(true);
        let response;

        if (editingAddress) {
          response = await api.put(`/addre/${editingAddress.id}`, formData);
        } else {
          response = await api.post('/addre', {
            ...formData,
            userId: userId
          });
        }

        await fetchUserAddresses();

        if (addresses.length === 0 || formData.isDefault) {
          setSelectedAddress(response.data.id);
        }

        setShowAddEditAddressModal(false);
        setEditingAddress(null);
      } catch (error) {
        handleApiError(error, 'Saving address');
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="modal-overlay">
        <div className="address-modal">
          <div className="modal-header">
            <h3>{editingAddress ? 'Adresse bearbeiten' : 'Neue Adresse hinzufügen'}</h3>
            <button
              onClick={() => {
                setShowAddEditAddressModal(false);
                setEditingAddress(null);
              }}
              className="close-modal"
            >
              <X size={20} />
            </button>
          </div>

          <div className="address-form">
            <div className="form-group">
              <label htmlFor="type">Adresstyp</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="address-type-select"
              >
                <option value="Home">Heim</option>
                <option value="Work">Arbeiten</option>
                <option value="Other">Andere</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="streetAddress">Straße und Hausnummer*</label>
              <input
                type="text"
                id="streetAddress"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleChange}
                placeholder="Musterstraße 12"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">Stadt*</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Berlin"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">Bundesland*</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="Bayern"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="zipCode">Postleitzahl*</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                placeholder="10115"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Telefonnummer*</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="+49 123 4567890"
                required
              />
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="isDefault"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleChange}
              />
              <label htmlFor="isDefault">Als Standardadresse festlegen</label>
            </div>
          </div>

          <div className="modal-actions">
            <button
              onClick={() => {
                setShowAddEditAddressModal(false);
                setEditingAddress(null);
              }}
              className="cancel-btn"
            >
              Abbrechen
            </button>

            <button
              onClick={handleSubmit}
              className="confirm-btn"
              disabled={
                !formData.streetAddress ||
                !formData.city ||
                !formData.state ||
                !formData.zipCode ||
                !formData.phoneNumber ||
                isLoading
              }
            >
              {isLoading ? (
                <>
                  <Loader className="spinner" size={16} />
                  Wird gespeichert...
                </>
              ) : (
                editingAddress ? 'Adresse aktualisieren' : 'Adresse speichern'
              )}
            </button>
          </div>
        </div>
      </div>

    );
  };

  // Address Dropdown Component
  const AddressDropdown = () => (
    <div className="address-dropdown-container">
      <div
        className="address-dropdown-header"
        onClick={() => setShowAddressDropdown(!showAddressDropdown)}
      >
        <div className="address-dropdown-selected">
          <MapPin size={16} className="address-icon" />
          {selectedAddress ? (
            <div className="selected-address-info">
              <span className="address-nickname">
                {addresses.find(a => a.id === selectedAddress)?.type || 'My Address'}
                {addresses.find(a => a.id === selectedAddress)?.isDefault && (
                  <span className="default-badge">Default</span>
                )}
              </span>
              <span className="address-details-text">
                {formatAddress(addresses.find(a => a.id === selectedAddress))}
              </span>
            </div>
          ) : (
            <span>Select delivery address</span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`dropdown-arrow ${showAddressDropdown ? 'rotated' : ''}`}
        />
      </div>

      {showAddressDropdown && (
        <div className="address-dropdown-list">
          {addresses.map(address => (
            <div
              key={address.id}
              className={`address-dropdown-item ${selectedAddress === address.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedAddress(address.id);
                setShowAddressDropdown(false);
              }}
            >
              <div className="address-item-header">
                <span className="address-type">{address.type}</span>
                {address.isDefault && <span className="default-badge">Default</span>}
              </div>
              <p className="address-full">{formatAddress(address)}</p>
            </div>
          ))}

          <div
            className="address-dropdown-item add-new"
            onClick={() => {
              setShowAddressDropdown(false);
              setShowAddEditAddressModal(true);
            }}
          >
            <PlusCircle size={16} style={{ marginRight: '8px' }} />
            Add New Address
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="order-food-app">
      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
          api={api}
        />
      )}

      {/* Address Selection Modal */}
      {showAddressModal && <AddressModal />}

      {/* Add/Edit Address Modal */}
      {showAddEditAddressModal && <AddEditAddressModal />}

      {!isRestaurantOpen() && (
        <div style={{
          backgroundColor: '#e80000',
          color: 'white',
          textAlign: 'center',
          padding: '10px',
          fontWeight: 'bold',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          {getRestaurantStatusMessage()}
        </div>
      )}

      <div className="main-container">
        <div className="menu-section">
          <div className="search-filter-container">
            <div className="search-box">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Suchmenü..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search">
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="filter-buttons">

              <button
                className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                Alle Gerichte
              </button>

              <span className="pickup-highlight">
                🛍️ Nur Abholung im Restaurant
              </span>


            </div>

          </div>

          <div className="menu-content">
            {filteredMenuData.length === 0 ? (
              <div className="no-results">
                <p>No dishes found matching your criteria.</p>
              </div>
            ) : (
              filteredMenuData.map((section) => (
                <div key={section.title} className="menu-category">
                  <div
                    className="category-header"
                    onClick={() => toggleCategory(section.title)}
                  >
                    <div className="category-info">
                      <span className="category-icon" style={{ color: section.color }}>
                        {section.icon}
                        {section.isVegetarian && (
                          <span className="vegetarian-category-badge">
                            <Leaf size={12} />
                          </span>
                        )}
                      </span>
                      <h2 className="category-title">
                        {section.title}
                        {section.isVegetarian && (
                          <span className="vegetarian-category-label">
                            <Leaf size={14} style={{ marginLeft: '8px' }} />
                          </span>
                        )}
                      </h2>
                    </div>
                    <span className="toggle-icon">
                      {activeCategory === section.title ? '−' : '+'}
                    </span>
                  </div>

                  <div className={`category-content ${activeCategory === section.title || activeCategory === null ? 'expanded' : ''}`}>
                    <div className="menu-items-grid">
                      {section.items.map((item) => (
                        <div key={item.id} className="menu-item-card">
                          {item.imageUrl && (
                            <div className="menu-item-image">
                              <img src={item.imageUrl} alt={item.name} />
                              {item.vegetarian && (
                                <span className="vegetarian-badge-overlay">
                                  <Leaf size={16} />
                                </span>
                              )}
                            </div>
                          )}
                          <div className="menu-item-header">
                            <h3 className="menu-item-name">{item.name}</h3>
                            <div className="menu-item-badges">
                              {item.vegetarian && (
                                <span className="badge vegetarian-badge">
                                  <Leaf size={12} />
                                  Veg
                                </span>
                              )}
                            </div>
                          </div>

                          {item.description && (
                            <p className="menu-item-description">{item.description}</p>
                          )}

                          <div className="menu-item-footer">
                            <span className="menu-item-price">{formatPrice(item.price)}</span>
                            <button
                              onClick={() => addToCart(item)}
                              className="add-to-cart-btn"
                              style={{ backgroundColor: section.color }}
                              disabled={isLoading || !isRestaurantOpen()}
                            >
                              <Plus size={16} />
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="cart-sidebar">
          <div className="cart-header">
            <h2 className="cart-title">Ihr Warenkorb</h2>
            {cart.length > 0 && (
              <span className="cart-count-badge">
                {cart.reduce((total, item) => total + item.quantity, 0)}
              </span>
            )}
          </div>

          <div className="cart-content">
            {cart.length === 0 ? (
              <div className="empty-cart">
                <div className="empty-cart-icon">
                  <Utensils size={48} />
                </div>
                <p className="empty-cart-title">Your cart is empty</p>
                <p className="empty-cart-message">Add delicious dishes to get started</p>
              </div>
            ) : (
              <>
                <div className="cart-items">
                  {cart.map((item) => (
                    <div key={item.id} className="cart-item">
                      <div className="cart-item-info">
                        <h4 className="cart-item-name">{item.itemName || item.name}</h4>
                        <div className="quantity-controls">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="quantity-btn"
                            disabled={item.quantity <= 1 || isLoading}
                          >
                            <Minus size={14} />
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="quantity-btn"
                            disabled={isLoading}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="cart-item-actions">
                        <span className="cart-item-price">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="remove-item-btn"
                          disabled={isLoading}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {userId && (
                  <div className="delivery-address-preview">
                    <h4>Telefonnummer</h4>
                    {addresses.length > 0 ? (
                      <div
                        className="address-preview-card"
                        onClick={() => setShowAddressModal(true)}
                      >
                        <MapPin size={16} className="address-icon" />
                        <div>
                          {selectedAddress ? (
                            <>
                              <p className="address-nickname">
                                {addresses.find(a => a.id === selectedAddress)?.type || 'My Address'}
                                {addresses.find(a => a.id === selectedAddress)?.isDefault && (
                                  <span className="default-badge">Default</span>
                                )}
                              </p>
                              <p>{formatAddress(addresses.find(a => a.id === selectedAddress))}</p>
                            </>
                          ) : (
                            <p>Select delivery address</p>
                          )}
                        </div>
                        <span className="change-address"> ändern</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddEditAddressModal(true)}
                        className="add-address-btn"
                      >
                        <PlusCircle size={16} style={{ marginRight: '8px' }} />
                        Adresse
                      </button>
                    )}
                  </div>
                )}

                <div className="cart-summary">
                  <div className="summary-row total-row">
                    <span>Gesamt  </span>
                    <span>{formatPrice(calculateTotal())}</span>
                  </div>

                  <button
                    onClick={proceedToPay}
                    className="checkout-btn"
                    disabled={isLoading || cart.length === 0 || (userId && addresses.length > 0 && !selectedAddress) || !isRestaurantOpen()}
                  >
                    {isLoading ? (
                      <>
                        <Loader className="spinner" size={16} />
                        Processing...
                      </>
                    ) : (
                      <>
                        {!userId && <Lock size={16} style={{ marginRight: '8px' }} />}
                        {userId
                          ? (addresses.length > 0 ?
                            (selectedAddress
                              ? `Checkout • ${formatPrice(calculateTotal())}`
                              : 'Select Address')
                            : `Checkout • ${formatPrice(calculateTotal())}`)
                          : (isRestaurantOpen() ? 'Login to Checkout' : 'Closed')}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default OrderFood;
// import React from 'react';
// import { Phone, Mail, X } from 'lucide-react';
// import './OrderFood.css';
// import { useNavigate } from 'react-router-dom';

// // ✅ REAL WhatsApp Icon (SVG)
// const WhatsAppIcon = ({ size = 24 }) => (
//   <svg
//     xmlns="http://www.w3.org/2000/svg"
//     width={size}
//     height={size}
//     fill="currentColor"
//     viewBox="0 0 24 24"
//   >
//     <path d="M20.52 3.48A11.8 11.8 0 0 0 12.04 0C5.66-.02.48 5.14.5 11.5c0 2.03.53 4.01 1.54 5.77L0 24l7-1.97a11.93 11.93 0 0 0 5.03 1.12h.01c6.36 0 11.53-5.16 11.55-11.52a11.47 11.47 0 0 0-3.07-7.15zM12.04 21.3h-.01a9.7 9.7 0 0 1-4.94-1.35l-.35-.2-4.15 1.18 1.18-4.04-.23-.37a9.53 9.53 0 0 1-1.48-5.14c-.02-5.24 4.25-9.51 9.48-9.53h.03c5.22 0 9.49 4.27 9.51 9.49-.02 5.23-4.28 9.46-9.5 9.46zm5.47-7.14c-.3-.15-1.77-.87-2.05-.96-.28-.1-.48-.15-.68.15-.2.29-.78.96-.96 1.16-.18.19-.35.21-.64.07-.3-.15-1.26-.46-2.4-1.47a9.03 9.03 0 0 1-1.67-2.07c-.17-.29-.02-.45.13-.6.13-.13.29-.35.44-.52.15-.18.2-.3.3-.5.1-.21.05-.39-.02-.55-.07-.15-.68-1.63-.93-2.23-.24-.58-.49-.5-.68-.51h-.58c-.2 0-.52.07-.79.36-.27.29-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.2 3.07.15.2 2.08 3.18 5.04 4.45.7.3 1.24.48 1.66.62.7.22 1.33.19 1.83.12.56-.08 1.77-.72 2.02-1.42.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35z"/>
//   </svg>
// );

// const OrderFood = () => {
//   const navigate = useNavigate();

//   const handleClose = () => {
//     navigate('/'); // Redirect to home page
//   };

//   return (
//     <div className="order-food-page">
//       <button className="page-close" onClick={handleClose}>
//         <X size={24} />
//       </button>

//       <div className="order-food-content">
//         <h1>Bitte rufen Sie an, um Ihr Essen zu bestellen!</h1>

//         <p className="label">Telefon</p>
//         <p className="phone">
//           <Phone size={22} /> 07552 3822 766
//         </p>

//         <p className="label">WhatsApp</p>
//         <a
//           href="https://wa.me/491722389459"
//           target="_blank"
//           rel="noopener noreferrer"
//           className="whatsapp"
//         >
//           <WhatsAppIcon size={24} />
//           <span className="whatsapp-text">+49 172 2389459</span>
//         </a>

//         <p className="label">E-Mail</p>
//         <p className="email">
//           <Mail size={22} /> info@papadam-pfullendorf.de
//         </p>

//         {/* ✅ Added text below Email */}
//         <p className="pickup-note">Kein Lieferung, nur Abholung</p>
//       </div>
//     </div>
//   );
// };

// export default OrderFood;

